import { Fragment, useId, type ReactNode } from 'react';
import './Segmented.css';

/**
 * Segmented — **짧은 선택지 몇 개 중 하나** (2026-09-15 · 대시보드 시안 v2 V0 · 정본 제안 B6-3 `#SEGMENT`).
 *
 * 쓰는 곳: 위젯 설정 「표시 개수 3건 · 5건 · 8건」 · 「기간 이번 달 · 올해」 · 「범위 나 · 팀 · 전사」 · 「폭 1/3 · 1/2 · 전체」.
 * 지금은 대시보드가 `dw-seg` · `dw-pill` 로 손으로 짠다.
 *
 * ★★★ **네이티브 라디오다** (정본 B6) — Tab 은 묶음을 한 정거장으로 보고 안에서는 ←→↑↓ 로 옮긴다.
 *     단추 묶음으로 만들면 그 키보드 규칙을 손으로 다시 짜야 하고, 스크린리더는 「몇 개 중 몇 번째」를 못 읽는다.
 * ★★ **막힌 선택지는 보이되 고를 수 없다** — 네이티브 `disabled` 라 화살표도 건너뛴다. 까닭은 그 줄에 붙여 읽어 주고
 *    (`aria-describedby`), 묶음 아래 한 줄(`hint`)로 눈에도 보인다(시안 「팀 · 전사는 근태 담당자 · 관리자만 고릅니다」).
 * ★ 선택지가 여섯을 넘거나 글자가 길면 `Select` 를 쓴다(정본 B6 「한눈에 다 보여야 할 때」).
 */

export interface SegmentedOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  /** 막힌 까닭 — 읽어 주고, 마우스를 올리면 보인다. */
  disabledReason?: string;
}

export interface SegmentedProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly SegmentedOption[];
  /** 무엇을 고르는지(스크린리더). 밖에 보이는 라벨이 있으면 `labelledBy` 를 준다. */
  ariaLabel?: string;
  labelledBy?: string;
  /** 묶음 아래 한 줄 — 막힌 까닭 · 도움말. */
  hint?: ReactNode;
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
  /** 라디오 name — 안 주면 만든다. */
  name?: string;
}

export function Segmented({
  value, onChange, options, ariaLabel, labelledBy, hint, size = 'md', disabled = false, className, name,
}: SegmentedProps) {
  const uid = useId();
  const groupName = name ?? `${uid}-seg`;
  const hintId = hint != null ? `${uid}-hint` : undefined;

  return (
    <div className={['wc-seg', `wc-seg--${size}`, className ?? ''].filter(Boolean).join(' ')}>
      <div
        role="radiogroup"
        aria-label={labelledBy ? undefined : ariaLabel}
        aria-labelledby={labelledBy}
        aria-describedby={hintId}
        aria-disabled={disabled || undefined}
        className="wc-seg__group"
      >
        {options.map((o) => {
          const off = disabled || o.disabled === true;
          const reasonId = o.disabled && o.disabledReason ? `${uid}-why-${o.value}` : undefined;
          return (
            <Fragment key={o.value}>
              <label
                className={['wc-seg__opt', o.value === value ? 'is-on' : '', off ? 'is-off' : ''].filter(Boolean).join(' ')}
                title={o.disabled ? o.disabledReason : undefined}
              >
                <input
                  type="radio"
                  className="wc-seg__input"
                  name={groupName}
                  value={o.value}
                  checked={o.value === value}
                  disabled={off}
                  aria-describedby={reasonId}
                  onChange={() => { if (!off) onChange(o.value); }}
                />
                <span className="wc-seg__face">{o.label}</span>
              </label>
              {/* ★ 까닭은 label **밖**에 둔다 — 안에 두면 이름(「팀」)에 섞여 「팀 팀 · 전사는…」으로 읽힌다. */}
              {reasonId && <span id={reasonId} className="wc-seg__sr">{o.disabledReason}</span>}
            </Fragment>
          );
        })}
      </div>
      {hint != null && <p id={hintId} className="wc-seg__hint">{hint}</p>}
    </div>
  );
}
